import type { ComponentProps, ReactNode } from 'react';

/**
 * Small, deliberately boring UI primitives. Keeping them here means the panels
 * read as layout rather than styling noise.
 *
 * React 19 passes `ref` as a normal prop, which `ComponentProps<'button'>`
 * already includes — no forwardRef ceremony needed.
 */

type ButtonProps = ComponentProps<'button'> & {
  variant?: 'default' | 'primary' | 'ghost';
};

export function Button({ variant = 'default', className = '', ...props }: ButtonProps) {
  const styles =
    variant === 'primary'
      ? 'bg-accent-strong text-accent-ink hover:brightness-110'
      : variant === 'ghost'
        ? 'bg-transparent hover:bg-surface-2'
        : 'bg-surface-2 hover:bg-surface-3 border border-line';
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${styles} ${className}`}
      {...props}
    />
  );
}

export function IconButton({
  label,
  className = '',
  active = false,
  ...props
}: ComponentProps<'button'> & { label: string; active?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      className={`inline-flex size-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent ${
        active ? 'bg-accent-soft text-ink' : ''
      } ${className}`}
      {...props}
    />
  );
}

export function PanelSection({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-b border-line">
      <header className="flex items-center justify-between px-3 pt-3 pb-1.5">
        <h2 className="text-[11px] font-semibold tracking-wide text-faint uppercase">{title}</h2>
        {actions ? <div className="flex items-center gap-1">{actions}</div> : null}
      </header>
      <div className="px-3 pb-3">{children}</div>
    </section>
  );
}

/**
 * A label plus its control. Renders a real `<label>` wrapping the control so the
 * association is implicit: a bare `<span>` left every number field in the app
 * unnamed to a screen reader, which axe flags as critical.
 */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children, which this rule cannot see through. The association is real (implicit label wrapping) and axe's `label` rule verifies it end to end in the E2E suite.
    <label className="flex items-center justify-between gap-2 py-1">
      <span className="text-muted">{label}</span>
      <div className="flex items-center gap-1.5">{children}</div>
    </label>
  );
}

type NumberFieldProps = Omit<ComponentProps<'input'>, 'onChange' | 'value'> & {
  value: number;
  onCommit: (value: number) => void;
};

/**
 * Number input that commits on blur or Enter rather than on every keystroke —
 * each commit is a history entry, so per-keystroke commits would flood undo.
 */
export function NumberField({ value, onCommit, className = '', ...props }: NumberFieldProps) {
  return (
    <input
      type="number"
      defaultValue={value}
      key={value}
      onBlur={(event) => {
        const next = Number(event.currentTarget.value);
        if (Number.isFinite(next) && next !== value) onCommit(next);
        else event.currentTarget.value = String(value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          event.currentTarget.value = String(value);
          event.currentTarget.blur();
        }
      }}
      className={`w-20 rounded-md border border-line bg-surface-2 px-2 py-1 text-right tabular-nums focus:border-accent focus:outline-none ${className}`}
      {...props}
    />
  );
}

export function EmptyState({ title, note }: { title: string; note?: string }) {
  return (
    <div className="rounded-md border border-dashed border-line px-3 py-6 text-center">
      <p className="text-muted">{title}</p>
      {note ? <p className="mt-1 text-[11px] text-faint">{note}</p> : null}
    </div>
  );
}
